import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  getInstallmentConfigThunk,
  getInstallmentPlansThunk,
  toggleInstallmentsThunk,
  initializeInstallmentConfigThunk,
  clearInstallmentError,
} from "../../../store/thunks/branchInstallmentThunks";

import {
  selectInstallmentConfig,
  selectInstallmentPlans,
  selectIsLoadingConfig,
  selectIsLoadingPlans,
  selectInstallmentError,
  selectIsInstallmentEnabled,
  selectActivePlans,
} from "../../../store/selectors/branchInstallmentSelectors";

import {
  CurrencyRupeeIcon,
  PlusIcon,
  CogIcon,
  ChartBarIcon,
  ShieldCheckIcon,
  ClockIcon,
  DocumentTextIcon,
} from "@heroicons/react/outline";
import InstallmentConfigSection from "./InstallmentConfigSection";
import InstallmentPlansList from "./InstallmentPlansList";
import InstallmentAnalytics from "./InstallmentAnalytics";
import CreateInstallmentPlanModal from "../modals/CreateInstallmentPlanModal";

const BranchInstallmentManagement = ({ branch }) => {
  const dispatch = useDispatch();
  const [activeSection, setActiveSection] = useState("overview");
  const [showCreatePlanModal, setShowCreatePlanModal] = useState(false);

  const config = useSelector(selectInstallmentConfig);
  const plans = useSelector(selectActivePlans);
  const isLoadingConfig = useSelector(selectIsLoadingConfig);
  const isLoadingPlans = useSelector(selectIsLoadingPlans);
  const error = useSelector(selectInstallmentError);
  const isEnabled = useSelector(selectIsInstallmentEnabled);

  const branchCode = branch?.code;

  useEffect(() => {
    if (branchCode) {
      dispatch(getInstallmentConfigThunk(branchCode));
      dispatch(getInstallmentPlansThunk({ branchCode, activeOnly: true }));
    }

    return () => {
      dispatch(clearInstallmentError());
    };
  }, [dispatch, branchCode]);

  const handleInitialize = async () => {
    if (
      window.confirm(
        "Initialize installment configuration for this branch? This will create default settings."
      )
    ) {
      try {
        await dispatch(initializeInstallmentConfigThunk(branchCode)).unwrap();
        alert("Installment configuration initialized successfully");
      } catch (error) {
        alert(`Failed to initialize: ${error}`);
      }
    }
  };

  const handleToggle = async () => {
    try {
      await dispatch(
        toggleInstallmentsThunk({
          branchCode,
          enabled: !isEnabled,
        })
      ).unwrap();
    } catch (error) {
      alert(`Failed to toggle installments: ${error}`);
    }
  };

  if (isLoadingConfig) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // Not initialized
  if (!config) {
    return (
      <div className="text-center py-12">
        <CurrencyRupeeIcon className="h-16 w-16 mx-auto text-gray-400 mb-4" />
        <h3 className="text-lg font-semibold text-gray-900 mb-2">
          Installment Configuration Not Initialized
        </h3>
        <p className="text-gray-600 mb-6">
          Set up installment payments for this branch to offer flexible payment
          options to customers.
        </p>
        <button
          onClick={handleInitialize}
          className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
        >
          Initialize Installment Configuration
        </button>
      </div>
    );
  }

  const sections = [
    { id: "overview", label: "Overview", icon: ChartBarIcon },
    { id: "plans", label: "Plans", icon: DocumentTextIcon },
    { id: "config", label: "Configuration", icon: CogIcon },
    { id: "analytics", label: "Analytics", icon: ChartBarIcon },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-lg border border-blue-200">
        <div className="flex justify-between items-start">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Installment Management
            </h2>
            <p className="text-gray-600">
              Configure and manage installment payment options for {branch.name}
            </p>
          </div>

          <div className="flex items-center gap-4">
            <div className="text-right mr-4">
              <p className="text-sm text-gray-600">Status</p>
              <p className="font-semibold">
                {isEnabled ? (
                  <span className="text-green-600">Enabled</span>
                ) : (
                  <span className="text-red-600">Disabled</span>
                )}
              </p>
            </div>

            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={isEnabled}
                onChange={handleToggle}
                className="sr-only peer"
              />
              <div className="w-14 h-7 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[4px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-blue-600"></div>
            </label>
          </div>
        </div>

        {/* Quick Stats */}
        {isEnabled && (
          <div className="grid grid-cols-4 gap-4 mt-6">
            <div className="bg-white p-4 rounded-lg">
              <p className="text-sm text-gray-600">Active Plans</p>
              <p className="text-2xl font-bold text-blue-600">
                {plans?.length || 0}
              </p>
            </div>
            <div className="bg-white p-4 rounded-lg">
              <p className="text-sm text-gray-600">KYC Required</p>
              <p className="text-2xl font-bold text-gray-900">
                {config?.kycRequirements?.mandatory ? "Yes" : "No"}
              </p>
            </div>
            <div className="bg-white p-4 rounded-lg">
              <p className="text-sm text-gray-600">Interest Method</p>
              <p className="text-lg font-semibold text-gray-900">
                {config?.interestConfig?.calculationMethod || "N/A"}
              </p>
            </div>
            <div className="bg-white p-4 rounded-lg">
              <p className="text-sm text-gray-600">Min Installment</p>
              <p className="text-xl font-bold text-gray-900">
                ₹
                {config?.paymentRules?.minInstallmentAmount?.toLocaleString() ||
                  0}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex justify-between items-start">
            <div className="flex items-start gap-3">
              <div className="text-red-600">
                <svg
                  className="h-5 w-5"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div>
                <h3 className="font-semibold text-red-800">Error</h3>
                <p className="text-sm text-red-700">{error}</p>
              </div>
            </div>
            <button
              onClick={() => dispatch(clearInstallmentError())}
              className="text-red-600 hover:text-red-800"
            >
              Dismiss
            </button>
          </div>
        </div>
      )}

      {/* Disabled State */}
      {!isEnabled && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
          <ShieldCheckIcon className="h-12 w-12 mx-auto text-yellow-600 mb-3" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            Installments Disabled
          </h3>
          <p className="text-gray-600 mb-4">
            Enable installments to start offering flexible payment options to
            customers.
          </p>
          <button
            onClick={handleToggle}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Enable Installments
          </button>
        </div>
      )}

      {/* Main Content - Only show when enabled */}
      {isEnabled && (
        <>
          {/* Section Tabs */}
          <div className="bg-white rounded-lg shadow-sm border">
            <div className="border-b">
              <nav className="flex -mb-px">
                {sections.map((section) => {
                  const Icon = section.icon;
                  return (
                    <button
                      key={section.id}
                      onClick={() => setActiveSection(section.id)}
                      className={`px-6 py-3 font-medium border-b-2 transition-colors flex items-center gap-2 ${
                        activeSection === section.id
                          ? "border-blue-600 text-blue-600"
                          : "border-transparent text-gray-600 hover:text-gray-900"
                      }`}
                    >
                      <Icon className="h-5 w-5" />
                      {section.label}
                    </button>
                  );
                })}
              </nav>
            </div>

            <div className="p-6">
              {/* Overview Section */}
              {activeSection === "overview" && (
                <div className="space-y-6">
                  {/* KYC Requirements */}
                  <div className="bg-blue-50 p-6 rounded-lg">
                    <div className="flex items-center gap-2 mb-4">
                      <ShieldCheckIcon className="h-6 w-6 text-blue-600" />
                      <h3 className="text-lg font-semibold">
                        KYC Requirements
                      </h3>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-gray-600">Mandatory</p>
                        <p className="font-semibold">
                          {config.kycRequirements?.mandatory ? "Yes" : "No"}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">
                          Min Order Value for KYC
                        </p>
                        <p className="font-semibold">
                          ₹
                          {config.kycRequirements?.minOrderValueForKYC?.toLocaleString()}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">
                          Accepted ID Types
                        </p>
                        <div className="flex flex-wrap gap-2 mt-1">
                          {config.kycRequirements?.acceptedIdTypes?.map(
                            (type) => (
                              <span
                                key={type}
                                className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full"
                              >
                                {type}
                              </span>
                            )
                          )}
                        </div>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">
                          Manual Verification
                        </p>
                        <p className="font-semibold">
                          {config.kycRequirements?.requiresManualVerification
                            ? "Required"
                            : "Not Required"}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Interest Configuration */}
                  <div className="bg-green-50 p-6 rounded-lg">
                    <div className="flex items-center gap-2 mb-4">
                      <CurrencyRupeeIcon className="h-6 w-6 text-green-600" />
                      <h3 className="text-lg font-semibold">
                        Interest Configuration
                      </h3>
                    </div>
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <p className="text-sm text-gray-600">
                          Calculation Method
                        </p>
                        <p className="font-semibold">
                          {config.interestConfig?.calculationMethod}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Default Rate</p>
                        <p className="font-semibold">
                          {config.interestConfig?.defaultRate?.annualRate}% p.a.
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">
                          Grace Period Days
                        </p>
                        <p className="font-semibold">
                          {config.interestConfig?.gracePeriodDays} days
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">
                          Penalty Interest
                        </p>
                        <p className="font-semibold">
                          {config.interestConfig?.penaltyInterest?.enabled
                            ? `+${config.interestConfig.penaltyInterest.additionalRate}%`
                            : "Disabled"}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Payment Rules */}
                  <div className="bg-purple-50 p-6 rounded-lg">
                    <div className="flex items-center gap-2 mb-4">
                      <ClockIcon className="h-6 w-6 text-purple-600" />
                      <h3 className="text-lg font-semibold">Payment Rules</h3>
                    </div>
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <p className="text-sm text-gray-600">
                          Min Installment Amount
                        </p>
                        <p className="font-semibold">
                          ₹
                          {config.paymentRules?.minInstallmentAmount?.toLocaleString()}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Due Date Policy</p>
                        <p className="font-semibold">
                          {config.paymentRules?.dueDatePolicy}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Fixed Due Date</p>
                        <p className="font-semibold">
                          Day {config.paymentRules?.fixedDueDate}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Grace Period</p>
                        <p className="font-semibold">
                          {config.paymentRules?.gracePeriod?.days} days
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Prepayment</p>
                        <p className="font-semibold">
                          {config.paymentRules?.prepayment?.allowed
                            ? "Allowed"
                            : "Not Allowed"}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Pause Policy */}
                  <div className="bg-yellow-50 p-6 rounded-lg">
                    <div className="flex items-center gap-2 mb-4">
                      <ClockIcon className="h-6 w-6 text-yellow-600" />
                      <h3 className="text-lg font-semibold">Pause Policy</h3>
                    </div>
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <p className="text-sm text-gray-600">Allowed</p>
                        <p className="font-semibold">
                          {config.pausePolicy?.allowed ? "Yes" : "No"}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">
                          Max Pauses Allowed
                        </p>
                        <p className="font-semibold">
                          {config.pausePolicy?.maxPausesAllowed}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">
                          Max Consecutive Months
                        </p>
                        <p className="font-semibold">
                          {config.pausePolicy?.maxConsecutivePauseMonths} months
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">
                          Requires Approval
                        </p>
                        <p className="font-semibold">
                          {config.pausePolicy?.requiresApproval ? "Yes" : "No"}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Plans Section */}
              {activeSection === "plans" && (
                <div>
                  <div className="flex justify-between items-center mb-6">
                    <div>
                      <h3 className="text-lg font-semibold">
                        Installment Plans
                      </h3>
                      <p className="text-sm text-gray-600">
                        {plans?.length || 0} active plan(s)
                      </p>
                    </div>
                    <button
                      onClick={() => setShowCreatePlanModal(true)}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
                    >
                      <PlusIcon className="h-5 w-5" />
                      Create New Plan
                    </button>
                  </div>

                  <InstallmentPlansList
                    branchCode={branchCode}
                    plans={plans}
                    isLoading={isLoadingPlans}
                  />
                </div>
              )}

              {/* Configuration Section */}
              {activeSection === "config" && (
                <InstallmentConfigSection
                  branchCode={branchCode}
                  config={config}
                />
              )}

              {/* Analytics Section */}
              {activeSection === "analytics" && (
                <InstallmentAnalytics branchCode={branchCode} />
              )}
            </div>
          </div>
        </>
      )}

      {/* Create Plan Modal */}
      {showCreatePlanModal && (
        <CreateInstallmentPlanModal
          branchCode={branchCode}
          onClose={() => setShowCreatePlanModal(false)}
          onSuccess={() => {
            setShowCreatePlanModal(false);
            dispatch(
              getInstallmentPlansThunk({ branchCode, activeOnly: true })
            );
          }}
        />
      )}
    </div>
  );
};

export default BranchInstallmentManagement;
